import { useLayoutEffect, useRef, useState } from 'react';

interface TabOption<T extends string> {
  value: T;
  label: string;
}

interface TabSelectorProps<T extends string> {
  options: TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  /** compact: small filter chips; lg: the 44 px desktop tabs from the redesign. */
  variant?: 'default' | 'compact' | 'lg';
}

const PADDING = {
  compact: 'px-3 py-1.5 text-xs',
  default: 'px-2.5 py-2.5 text-sm leading-5',
  lg: 'h-11 px-2.5 text-base leading-6',
};

/**
 * Pill tabs from the redesign: glass track, the active tab outlined in brand
 * green. The outline slides to the tab that was picked.
 */
export function TabSelector<T extends string>({
  options,
  value,
  onChange,
  className = '',
  variant = 'default',
}: TabSelectorProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef(new Map<T, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  const [animate, setAnimate] = useState(false);
  const labelsKey = options.map((option) => option.label).join('|');

  useLayoutEffect(() => {
    const measure = () => {
      const button = buttonRefs.current.get(value);
      if (!button) return setIndicator(null);
      setIndicator({ left: button.offsetLeft, width: button.offsetWidth });
    };
    measure();
    // Watch the track and every tab, so font loads and label changes re-place it.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && trackRef.current) observer.observe(trackRef.current);
    buttonRefs.current.forEach((button) => observer?.observe(button));
    return () => observer?.disconnect();
  }, [value, labelsKey]);

  // Slide only after the first placement, so the outline does not fly in on mount.
  useLayoutEffect(() => {
    if (indicator && !animate) {
      const id = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(id);
    }
  }, [indicator, animate]);

  return (
    <div ref={trackRef} className={`glass relative flex gap-1 ${className}`}>
      {indicator && (
        <span
          aria-hidden
          className={`pill-active edge-glow pointer-events-none absolute inset-y-0 ${
            animate ? 'transition-[left,width] duration-300 ease-out' : ''
          }`}
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              if (node) buttonRefs.current.set(option.value, node);
              else buttonRefs.current.delete(option.value);
            }}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.value)}
            className={`relative z-[1] flex flex-1 items-center justify-center whitespace-nowrap rounded-[12px] border border-transparent font-semibold transition-colors duration-300 ${PADDING[variant]} ${
              isActive ? 'text-[#fafafa]' : 'text-[#a3a3a3] hover:text-text-primary'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
